import {
  formatLocalDateTime,
  formatLocalDateTimeShort,
  parseUtcInstant,
} from "./dateTimeUtils";
import { formatCmWithMeters } from "./waterLevelFormatUtils";

function isPresent(value) {
  return value !== undefined && value !== null && value !== "";
}

function collectPayloadRoots(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return [];
  }
  const roots = [payload];
  for (const key of ["data", "telemetry", "payload"]) {
    const nested = payload[key];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      roots.push(nested);
    }
  }
  return roots;
}

function pickField(roots, keys) {
  for (const root of roots) {
    for (const key of keys) {
      if (isPresent(root[key])) return root[key];
    }
  }
  return null;
}

/** Map WebSocket row + payload to structured table columns. */
export function resolveTelemetryDisplay(row) {
  const payload = row?.payload ?? {};
  const roots = collectPayloadRoots(payload);

  const pick = (socketKey, payloadKeys) =>
    row?.[socketKey] ?? pickField(roots, payloadKeys);

  return {
    deviceId: row?.deviceId,
    deviceRole: row?.deviceRole ?? row?.device_role ?? null,
    topic: row?.topic,
    receivedAt: row?.receivedAt,
    deviceTimestamp: pick("deviceTimestamp", ["deviceTimestamp", "device_timestamp"]),
    timestamp: pick("timestamp", ["timestamp"]),
    startTime: pick("startTime", ["start_time", "startTime"]),
    endTime: pick("endTime", ["end_time", "endTime"]),
    durationSec: pick("durationSec", ["duration_sec", "durationSec"]),
    confirmedDistAvgCm: pick("confirmedDistAvgCm", [
      "confirmed_dist_avg_cm",
      "confirmedDistAvgCm",
    ]),
    waterLevelAvgCm: pick("waterLevelAvgCm", [
      "water_level_avg_cm",
      "waterLevelAvgCm",
    ]),
    confirmedLevelAvgCm: pick("confirmedLevelAvgCm", [
      "confirmed_level_avg_cm",
      "confirmedLevelAvgCm",
    ]),
    rawDistanceCm: pick("rawDistanceCm", ["raw_distance_cm", "rawDistanceCm"]),
    strength: pick("strength", ["strength"]),
    confidencePct: pick("confidencePct", ["confidence_pct", "confidencePct"]),
    referenceHeightCm: pick("referenceHeightCm", [
      "reference_height_cm",
      "referenceHeightCm",
    ]),
    dataValid: pick("dataValid", ["data_valid", "dataValid"]),
  };
}

/** Confirmed WL — `confirmed_level_avg_cm` only (no water_level_avg fallback). */
export function getConfirmedWaterLevelCm(row) {
  const display =
    row?.confirmedLevelAvgCm !== undefined
      ? row
      : resolveTelemetryDisplay(row);
  const confirmed = display?.confirmedLevelAvgCm;
  if (confirmed === 0 || confirmed === "0") return 0;
  if (confirmed == null || confirmed === "") return null;
  const num = Number(confirmed);
  return Number.isFinite(num) ? num : null;
}

/** Raw averaged WL — `water_level_avg_cm` only (no confirmed fallback). */
export function getWaterLevelAvgCm(row) {
  const display =
    row?.waterLevelAvgCm !== undefined ? row : resolveTelemetryDisplay(row);
  const avg = display?.waterLevelAvgCm;
  if (avg === 0 || avg === "0") return 0;
  if (avg == null || avg === "") return null;
  const num = Number(avg);
  return Number.isFinite(num) ? num : null;
}

/** Stable key for deduplicating historical and live telemetry rows. */
export function telemetryRecordKey(row) {
  const display = resolveTelemetryDisplay(row);
  if (display.deviceId && display.timestamp != null) {
    return `${display.deviceId}|${display.timestamp}`;
  }
  if (row?.id) return String(row.id);
  return `${display.deviceId}|${display.timestamp ?? ""}`;
}

/** Display UTC API timestamps in the user's local timezone. */
export function formatDateTime(value) {
  return formatLocalDateTime(value);
}

export function formatUtcDateTime(value) {
  return formatLocalDateTime(value);
}

/** Table "Timestamp" column — payload `timestamp` (stored as received_at). */
export function formatTelemetryTimestamp(row) {
  if (!row) return "—";
  return formatLocalDateTime(row.timestamp, { fallback: "—" });
}

export function formatDeviceTimestamp(value) {
  return formatLocalDateTime(value);
}

export function formatNumber(value, suffix = "") {
  const normalizedSuffix = String(suffix ?? "").trim();
  if (normalizedSuffix === "cm") {
    if (!isPresent(value) && value !== 0 && value !== "0") return "—";
    const num = Number(value);
    const cmDecimals =
      !Number.isFinite(num) || Number.isInteger(num) ? 0 : 2;
    return formatCmWithMeters(value, { cmDecimals });
  }
  if (value === 0 || value === "0") {
    return `0${suffix}`;
  }
  if (!isPresent(value)) return "—";
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return `${Number.isInteger(num) ? num : num.toFixed(2)}${suffix}`;
  }
  return `${value}${suffix}`;
}

export function formatDataValid(value) {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return { label: "Valid", valid: true };
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return { label: "Invalid", valid: false };
  }
  return { label: "—", valid: null };
}

function pickFirstReadingValue(values) {
  for (const value of values) {
    if (value === 0 || value === "0") return Number(value);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

const LATEST_READING_META_KEYS = new Set([
  "deviceId",
  "topic",
  "receivedAt",
  "timestamp",
  "startTime",
  "endTime",
]);

/** Newest row with gaps filled from recent readings (socket rows can be sparse). */
export function mergeLatestLidarReadings(rows) {
  if (!rows?.length) return null;

  const sorted = [...rows].sort(
    (a, b) =>
      (parseUtcInstant(b.timestamp)?.valueOf() ?? 0) -
      (parseUtcInstant(a.timestamp)?.valueOf() ?? 0),
  );

  const displays = sorted.map((row) => resolveTelemetryDisplay(row));
  const merged = { ...displays[0] };

  for (const key of Object.keys(merged)) {
    if (LATEST_READING_META_KEYS.has(key)) continue;
    merged[key] = pickFirstReadingValue(displays.map((display) => display[key]));
  }

  return merged;
}

export { formatLocalDateTime, formatLocalDateTimeShort, parseUtcInstant };
